import {
  BufferGeometry,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  Intersection,
  Line,
  LineBasicMaterial,
  MeshBasicMaterial,
  Object3D,
  Raycaster,
  Texture,
  Triangle,
  Vector3,
} from "three";
import { Font } from "three/examples/jsm/loaders/FontLoader";
import font from "../../../fonts/RoundedMplus1c_Regular.json";
import * as Config from "../config/ViewerConfig";
import { SCENE_TYPE } from "../config/ViewerConfig";
import { AutoAttenuablePin } from "../object/AutoAttenuablePin";
import { AutoAttenuatableSphere } from "../object/AutoAttenuatableSphere";
import { AutoAttenuatableTextSprite } from "../object/AutoAttenuatableTextSprite";
import { ExtendedLineLoop } from "../object/ExtendedLineLoop";
import { ExtendedMesh } from "../object/ExtendedMesh";
import { triangulateXY } from "../utils/utils";
import { Viewer } from "../viewer";
import {
  IMeasurementHandler,
  IMeasurementVisibleParams,
  IPanelDraggable,
  MeasurementMode,
  MeasurementState,
  MeasurementType,
} from "./types";
import { getRoundRect, polygonInteriorAnchor } from "./utils";

export class FlatAreaCalculationMeasurement
  implements IMeasurementHandler, IPanelDraggable
{
  type: MeasurementType = MeasurementType.FLAT_AREA_CALCULATION;
  _id: number;
  _points: Vector3[];
  value: number;

  private _group: Group;

  private _viewer: Viewer;
  private _mode: MeasurementMode;

  private _pins: AutoAttenuablePin[];
  // private _lineGeometry: BufferGeometry;
  private _lineMaterial: LineBasicMaterial;
  private _line: ExtendedLineLoop;

  // private _meshGeometry: BufferGeometry;
  private _meshIndices: number[];
  private _meshMaterial: MeshBasicMaterial;
  private _mesh: ExtendedMesh;
  // private _sphereGeometry: SphereBufferGeometry;
  private _sphereMaterial: MeshBasicMaterial;
  private _spheres: AutoAttenuatableSphere[];

  private _font: Font;
  // private _textGeometry: TextGeometry;
  // private _textMaterial: SpriteMaterial;
  private _textSprite: AutoAttenuatableTextSprite;

  private _textTextureContext: CanvasRenderingContext2D;
  private _textTexture: Texture;
  private _isSelected = false;

  private _textOffset: Vector3 = new Vector3();

  private _leaderLine: Line;
  private _leaderLineMaterial: LineBasicMaterial;
  private _leaderLineGeometry: BufferGeometry;

  public get id(): number {
    return this._id;
  }

  public get textOffset(): Vector3 {
    return this._textOffset.clone();
  }

  public set textOffset(offset: Vector3) {
    this._textOffset.copy(offset);
  }

  public get panelPosition(): Vector3 {
    return this._textSprite.getPosition().clone();
  }

  public hitPanel(ray: Raycaster): Intersection[] {
    if (!this._textSprite.visible) return [];
    this._textSprite.geometry.computeBoundingBox();
    this._textSprite.geometry.computeBoundingSphere();
    return ray.intersectObject(this._textSprite, true);
  }


  private calculateAnchor(): Vector3 {
    return polygonInteriorAnchor(this._points, this._meshIndices);
  }

  private calculateTextPosition(): Vector3 {
    return this.calculateAnchor().add(this._textOffset);
  }

  private updateLeaderLine() {
    if (this._points.length === 0) return;
    const from = this.calculateAnchor();
    const to = this._textSprite.getPosition();
    const positions = this._leaderLineGeometry.attributes[
      "position"
    ] as Float32BufferAttribute;
    positions.setXYZ(0, from.x, from.y, from.z);
    positions.setXYZ(1, to.x, to.y, to.z);
    positions.needsUpdate = true;
    this._leaderLineGeometry.computeBoundingSphere();
  }

  public updateTextPosition() {
    if (this._points.length > 2) {
      this._textSprite.setPosition(this.calculateTextPosition());
    }
    if (this._leaderLine.visible) {
      this.updateLeaderLine();
    }
    this._viewer.updateRenderer();
  }

  // Reset: move the panel back to the anchor
  public resetPanelPosition() {
    this._textOffset.set(0, 0, 0);
    this.updateTextPosition();
  }

  // Place the panel at world coordinates (restore saved position / external input)
  public setPanelPosition(position: Vector3) {
    if (this._points.length === 0) return;
    this._textOffset.copy(position.clone().sub(this.calculateAnchor()));
    this.updateTextPosition();
  }

  constructor(viewer: Viewer, textScale: Config.ITextScale) {
    this._viewer = viewer;
    this.value = 0;
    this._points = [];
    this._group = new Group();
    this._viewer.getScene(SCENE_TYPE.MEASUREMENT).add(this._group);
    this._mode = MeasurementMode.CREATE;
    this._meshIndices = [];
    this.appearanceState = "default";

    // this._lineGeometry = new BufferGeometry();
    this._lineMaterial = new LineBasicMaterial({
      color: Config.NORMAL_MEASUREMENT_COLOR,
      linewidth: 3,
      depthTest: false,
    });
    this._line = new ExtendedLineLoop(this._lineMaterial);
    this._line.visible = false;
    this._group.add(this._line);

    // this._meshGeometry = new BufferGeometry();
    this._meshMaterial = new MeshBasicMaterial({
      color: Config.NORMAL_MEASUREMENT_COLOR,
      side: DoubleSide,
      opacity: 0.5,
      transparent: true,
    });
    this._mesh = new ExtendedMesh(this._meshMaterial);
    this._group.add(this._mesh);

    this._pins = [];
    // this._sphereGeometry = new SphereBufferGeometry(1, 32, 16);
    this._sphereMaterial = new MeshBasicMaterial({
      color: Config.NORMAL_MEASUREMENT_COLOR,
    });
    this._spheres = [];

    this._font = new Font(font);

    const textCanvas = document.createElement("canvas");
    textCanvas.width = 1000;
    textCanvas.height = 500;
    this._textTextureContext = textCanvas.getContext("2d");
    this._textTexture = new Texture(textCanvas);
    this._textTexture.needsUpdate = true;
    this._textSprite = new AutoAttenuatableTextSprite(
      this._textTexture,
      textScale
    );
    this._textSprite.visible = false;
    this._group.add(this._textSprite);

    // Leader line connecting the panel to the anchor while hovered/selected
    this._leaderLineMaterial = new LineBasicMaterial({
      color: 0xffffff,
      transparent: false,
      depthTest: false,
    });
    this._leaderLineGeometry = new BufferGeometry();
    this._leaderLineGeometry.setAttribute(
      "position",
      new Float32BufferAttribute(new Float32Array(6), 3)
    );
    this._leaderLine = new Line(
      this._leaderLineGeometry,
      this._leaderLineMaterial
    );
    this._leaderLine.renderOrder = 1; 
    this._leaderLine.visible = false;
    this._group.add(this._leaderLine);
  }
  appearanceState: MeasurementState;
  hitText(ray: Raycaster): Intersection[] {
    this._textSprite.updateMatrixWorld(true);
    const objectList = [this._textSprite, this._mesh];
    objectList.forEach((object) => {
      object.updateMatrixWorld(true);
      object.geometry.computeBoundingBox();
      object.geometry.computeBoundingSphere();
    });

    return ray.intersectObjects(objectList, true);
  }

  private updateTextContext2d(state: MeasurementState) {
    const canvasWidth = this._textTextureContext.canvas.width;
    const canvasHeight = this._textTextureContext.canvas.height;
    this._textSprite.geometry.computeBoundingBox();
    this._textTextureContext.clearRect(0, 0, canvasWidth, canvasHeight);

    const borderThickness = 4;
    const fontsize = 44;
    const fontface = "Arial";
    const text = `${this.value.toFixed(3)} m²`;
    const tab1Width = 16;
    const edgeConst = 1.05;

    this._textTextureContext.font = fontsize + "px " + fontface;

    const textHeight = fontsize;

    const textWidth = this._textTextureContext.measureText(text).width;

    this._textTextureContext.lineWidth = borderThickness;
    this._textTextureContext.fillStyle = {
      default: Config.DEFAULT_MEASUREMENT_TEXT_BGCOLOR,
      selected: Config.SELECTED_MEASUREMENT_TEXT_BGCOLOR,
      hovered: Config.HOVERED_MEASUREMENT_TEXT_BGCOLOR,
    }[state];

    this._textTextureContext.strokeStyle = "rgba(0,0,0,1.0)"; //
    const roundedRectWidth = tab1Width + textWidth + borderThickness * 2;
    const roundedRectHeight = textHeight + borderThickness * 2;
    getRoundRect(
      this._textTextureContext,
      borderThickness / 2,
      borderThickness / 2,
      roundedRectWidth,
      roundedRectHeight,
      6
    );

    this._textTextureContext.fillStyle = "rgba(255,255,255,1.0)"; //text color
    this._textTextureContext.strokeStyle = "rgba(255,255,255,1.0)"; //text color

    this._textTextureContext.fillText(text, tab1Width, textHeight);

    // テキストテクスチャが間違いなく収まる幅へ定数で調整
    const newCanvasWidth = Math.ceil(roundedRectWidth * edgeConst);
    const newCanvasHeight = Math.ceil(roundedRectHeight * edgeConst);

    const geom = this._textSprite.geometry;
    const uvs = geom.attributes["uv"];
    uvs.setXY(0, 0, 1 - newCanvasHeight / canvasHeight);
    uvs.setXY(
      1,
      newCanvasWidth / canvasWidth,
      1 - newCanvasHeight / canvasHeight
    );
    uvs.setXY(2, newCanvasWidth / canvasWidth, 1);
    uvs.setXY(3, 0, 1);
    uvs.needsUpdate = true;
    this._textSprite.aspect = newCanvasHeight / newCanvasWidth;

    this._textTexture.needsUpdate = true;
  }

  private calculateArea() {
    const indices = triangulateXY(this._points);
    this._meshIndices = indices;
    const triangle = new Triangle();
    this.value = 0;
    for (let i = 0; i < indices.length; i += 3) {
      triangle.setFromPointsAndIndices(
        this._points,
        indices[i],
        indices[i + 1],
        indices[i + 2]
      );
      this.value += triangle.getArea();
    }
  }

  public setPoints(points: Vector3[]) {
    const anchorBefore =
      this._points.length > 2 && this._points.length === points.length
        ? this.calculateAnchor()
        : undefined;
    this._points = points.map((p) => p.clone());
    if (anchorBefore) {
      this._meshIndices = triangulateXY(this._points);
      this._textOffset.add(anchorBefore.sub(this.calculateAnchor()));
    }
    this._id =
      this._id === undefined ? this._viewer.generateMeasurementId() : this._id;
    // this._mode = MeasurementMode.INACTIVE;
    this.disposeSpheres();
    this.disposePins();
    for (const point of this._points) {
      const sphere = new AutoAttenuatableSphere(
        this._sphereMaterial,
        Config.MEASUREMENT_SPHERE_SIZE
      );
      sphere.position.copy(point);
      this._group.add(sphere);
      this._spheres.push(sphere);
    }

    for (let i = 0; i < this._points.length; ++i) {
      const pin = new AutoAttenuablePin(`${i}`, this._viewer);
      pin.setPosition(this._points[i]);
      this._group.add(pin);
      this._pins.push(pin);
    }

    this.calculateArea();
    this.render();
  }

  private disposeSpheres() {
    for (let sphere of this._spheres) {
      // this._group.remove(sphere);
      sphere.dispose();
      sphere = undefined;
    }
    this._spheres.length = 0;
  }

  private disposePins() {
    for (let pin of this._pins) {
      this._group.remove(pin);
      pin.geometry.dispose();
      pin.geometry = undefined;
      pin.material.dispose();
      pin.material = undefined;
      pin = undefined;
    }
    this._pins.length = 0;
  }

  public dispose() {
    this._points.length = 0;

    this._group.remove(this._mesh);
    this._group.remove(this._line);
    this._group.remove(this._textSprite);
    this._group.remove(this._leaderLine);

    this._leaderLineGeometry.dispose();
    this._leaderLineGeometry = undefined;
    this._leaderLineMaterial.dispose();
    this._leaderLineMaterial = undefined;
    this._leaderLine = undefined;

    // this._meshGeometry.dispose();
    // this._meshGeometry = undefined;
    this._meshMaterial.dispose();
    this._meshMaterial = undefined;
    this._mesh = undefined;

    // this._lineGeometry.dispose();
    // this._lineGeometry = undefined;
    this._lineMaterial.dispose();
    this._lineMaterial = undefined;
    this._line = undefined;

    // this._sphereGeometry.dispose();
    this.disposePins();
    this.disposeSpheres();
    this._sphereMaterial.dispose();
    // this._sphereGeometry = undefined;
    this._sphereMaterial = undefined;

    this._textSprite.dispose();
    this._textSprite = undefined;

    this._viewer.updateRenderer();
  }

  public render() {
    this._line.prepareRendering(this._points);
    this._line.visible = true;

    this._mesh.prepareRendering(this._points, this._meshIndices);

    for (const pin of this._pins) {
      pin.visible = false;
    }

    for (const sphere of this._spheres) {
      sphere.visible = false;
    }

    switch (this._mode) {
      case MeasurementMode.EDIT:
        this._lineMaterial.color.setHex(Config.EDIT_MEASUREMENT_COLOR);
        this._meshMaterial.color.setHex(Config.EDIT_MEASUREMENT_COLOR);
        this._points.forEach((val, i) => this._pins[i].setPosition(val));
        for (const pin of this._pins) {
          pin.visible = true;
        }
        break;
      default:
        this._lineMaterial.color.setHex(Config.NORMAL_MEASUREMENT_COLOR);
        this._meshMaterial.color.setHex(Config.NORMAL_MEASUREMENT_COLOR);

        this._points.forEach((val, i) => this._spheres[i].position.copy(val));
        for (const sphere of this._spheres) {
          sphere.visible = true;
        }
    }

    if (this._points.length > 2) {
      this.updateTextContext2d(this.appearanceState);
      this._textTexture.needsUpdate = true;
      this._textSprite.setPosition(this.calculateTextPosition());
      this._textSprite.visible = true;
      if (this._leaderLine.visible) {
        this.updateLeaderLine();
      }
    }
    this._viewer.updateRenderer();
  }

  public switchMode(mode: MeasurementMode) {
    this._mode = mode;
    this.render();
  }

  public onPositionUpdate(id: number, position: Vector3) {
    // Keep the panel still while dragging a vertex (same counter-adjustment as setPoints)
    const anchorBefore =
      this._points.length > 2 ? this.calculateAnchor() : undefined;
    this._points[id].copy(position);
    if (anchorBefore) {
      // Rebuild the mesh before the "after" anchor (see setPoints)
      this._meshIndices = triangulateXY(this._points);
      this._textOffset.add(anchorBefore.sub(this.calculateAnchor()));
    }
    this.calculateArea();
    this.render();
  }

  public onUpdateComplete(id: number, position: Vector3) {
    this._viewer.setPointsToMeasurement(this._id, this._points);
  }

  public getPickableObjects(): Object3D[] {
    return this._pins;
  }

  public setVisibility(params: IMeasurementVisibleParams) {
    if (params.text !== undefined) {
      this._textSprite.visible = params.text;
    }
    if (params.measurement !== undefined) {
      this._group.visible = params.measurement;
    }
    this._viewer.updateRenderer();
  }

  public getVisibility(): boolean {
    return this._group.visible;
  }

  public changeAppearanceState(state: MeasurementState): void {
    this.appearanceState = state;
    for (const sphere of this._spheres) {
      sphere.setMaterialByState(state);
      sphere.setMaterialByState(state);
    }

    this._line.setMaterialByState(state);
    this._mesh.setMaterialByState(state);

    this.updateTextContext2d(state);

    this._textSprite.changeRenderOrderByState(state);

    // Show the panel→anchor leader line while hovered or selected
    if (this._leaderLine) {
      this._leaderLine.visible =
        state !== "default" && this._textSprite.visible;
      if (this._leaderLine.visible) {
        this.updateLeaderLine();
      }
    }
  }
}
